import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useSelector } from "react-redux";
import { IGoals, TCommonRowData } from "@/ts/common-types";
import { partition } from "underscore";
import { GOAL_STATUS_CONFIG } from "@/ts/constants";

const GoalStatus: React.FC = () => {
    const goalData = useSelector(
        (state: IGoals) => state.goalData.value
    ) as unknown as TCommonRowData;

    const [achievedGoalsCount, setAchievedGoalsCount] = useState<number>(0);
    const [unAchievedGoalsCount, setUnAchievedGoalsCount] = useState<number>(0);
    const [borderWidth, setBorderWidth] = useState<number>(0);

    useEffect(() => {
        const [achievedGoals, unAchievedGoals] = partition(goalData, 'is_achieved');
        setAchievedGoalsCount(achievedGoals.length);
        setUnAchievedGoalsCount(unAchievedGoals.length);

        setBorderWidth(unAchievedGoals.length && unAchievedGoals.length ? 8 : 0);
    }, [goalData])

    const goalStatusData = [
        { name: "Achieved", y: achievedGoalsCount, color: GOAL_STATUS_CONFIG.achieved.color },
        { name: "Not Achieved", y: unAchievedGoalsCount, color: GOAL_STATUS_CONFIG.unachieved.color }, 
    ];

    const total = goalStatusData.reduce((sum, point) => sum + point.y, 0);

    const options: Highcharts.Options = {
        chart: {
            plotBackgroundColor: null,
            plotBorderWidth: null,
            plotShadow: false,
            type: "pie",
            animation: false,
            height: 250,
            width: null,
        },
        title: {
            text: "",
        },
        plotOptions: {
            pie: {
                innerSize: "55%",
                borderWidth: borderWidth,
                dataLabels: {
                    enabled: false,
                },
                showInLegend: false,
            },
        },
        legend: {
            align: "center",
            verticalAlign: "bottom",
            layout: "vertical",
            itemMarginTop: 5,
            itemMarginBottom: 5,
            symbolHeight: 10,
            symbolWidth: 10,
        },
        credits: {
            enabled: false,
        },
        series: [
            {
                type: "pie",
                data:
                    total === 0
                        ? [{ name: "No Data", y: 1, color: "#d3d3d3" }]
                        : goalStatusData,
            },
        ],
        tooltip: {
            enabled: false,
        },
    };

    const customLegendStyles = {
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "center" as const,
        marginTop: "20px",
        width: "100%",
    };

    const legendItemStyles = {
        display: "flex",
        justifyContent: "space-between" as const,
        alignItems: "center" as const,
        width: "100%",
        maxWidth: "350px",
        marginBottom: "5px",
    };

    const symbolStyles = (color: string) => ({
        width: "6px",
        height: "25px",
        borderRadius: "10px",
        backgroundColor: color,
        marginRight: "10px",
    });

    const containerProps = {
        style: {
            height: "250px",
            width: "300px",
            maxWidth: "400px",
        },
    };

    const legendTextStyles = {
        display: "flex",
        alignItems: "center" as const,
    };

    return (
        <div style={{
            overflow: "hidden",
            width: "100%",
        }}>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                }}
            >
                <div>
                    <HighchartsReact
                        highcharts={Highcharts}
                        options={options}
                        containerProps={containerProps}
                    />
                </div>
                <div style={customLegendStyles}>
                    {goalStatusData.map((point, index) => (
                        <div key={index} style={legendItemStyles}>
                            <div style={legendTextStyles}>
                                <div style={symbolStyles(point.color)}></div>
                                <div>{point.name}</div>
                            </div>
                            <div>{total === 0 ? 0 : point.y}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default GoalStatus;
